import React, { useCallback, useEffect, useState } from 'react';

import { CircularProgress, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CONTROLLER_ON } from '../../../../constants/colors';
import { BETTERFLIES_RANKING_PAGE_SIZE } from '../../../../constants/metrics';
import { CLICK_ACTION, SELECT_RANKING_CATEGORY } from '../../../../constants/tagManager';
import { IRankingDataItem, IRankingCard } from '../../../../interfaces/cards';
import { ITabBarItems } from '../../../../interfaces/tabbars';
import TagManagerService from '../../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import { TRANSLATIONS_HOME } from '../../../../translations/home';
import { TRANSLATIONS_METRICS_NE } from '../../../../translations/metrics';
import { toLocaleNumberFormat } from '../../../../utils/utils';
import { isEmptyObject } from '../../../../utils/validation/validation';
import BdsCircleIndicatorPictogram from '../../BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import BdsDivider from '../../Globals/BdsDivider/BdsDivider';
import InfiniteScroll from '../../InfiniteScroll/InfiniteScroll';
import TabBar from '../../TabBars/TabBar/TabBar';
import EmptyCard from '../EmptyCard/EmptyCard';
import ErrorCard from '../ErrorCard/ErrorCard';

import './RankingCard.scss';

const RankingCard = ({
  title,
  valueText,
  headButton,
  hasTabBar,
  tabBarIcons,
  data = {},
  loading,
  error,
  gtmTab,
  action,
  emptyRankingCard,
  isPercentage = false,
  suffixIconType = 'default',
  highlightTopThree = true,
  fetchPage,
  pageSize = BETTERFLIES_RANKING_PAGE_SIZE
}: IRankingCard) => {
  const [rankingData, setRankingData] = useState<IRankingDataItem[]>();
  const [tabBarItems, setTabBarItems] = useState<ITabBarItems[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [pagedListKey, setPagedListKey] = useState(0);

  const { t } = useTranslation();

  const changeTab = (index: number, value: string) => {
    setTabIndex(index);
    const selectedValue = Object.values(data).find((item) => item.name === value);
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_RANKING_CATEGORY, { option: selectedValue?.name, tab: gtmTab });
    setRankingData(selectedValue?.data);
  };

  const getTabBarItems = useCallback(() => {
    const tabItems = Object.values(data).map((category) => {
      return {
        icon: tabBarIcons ? tabBarIcons[category.id] : undefined,
        value: category.name
      };
    });
    setTabBarItems(tabItems);
  }, [data]);

  const tabBarParams = {
    items: tabBarItems,
    action: changeTab,
    value: tabIndex
  };

  useEffect(() => {
    if (data && hasTabBar) {
      getTabBarItems();
    }
    setRankingData(data?.[Object.keys(data)[0]]?.data);
  }, [data, hasTabBar, getTabBarItems]);

  const renderRankingHeader = () => {
    return (
      <div className="flex flex-row justify-between mb-2">
        <div className="title">{title}</div>
        {headButton && <div className="head-button">{headButton}</div>}
      </div>
    );
  };

  const renderCardContent = (content: React.ReactNode) => {
    return (
      <div className="ranking-card-content" data-experience="new">
        <div className="flex flex-column bg-white ranking-card-content-space">
          {renderRankingHeader()}
          {content}
        </div>
      </div>
    );
  };

  const renderErrorCard = (retryAction?: () => void) => (
    <ErrorCard
      primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
      secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
      action={retryAction}
      buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
    />
  );

  const renderEmptyCard = () =>
    emptyRankingCard || (
      <EmptyCard
        primaryText={t(TRANSLATIONS_HOME.rankingCard.emptyState.emptyStateTitle, { count: 2 })}
        secondaryText={t(TRANSLATIONS_HOME.rankingCard.emptyState.emptyStateDescription, { count: 2 })}
      />
    );

  if (loading) {
    return (
      <div className="ranking-skeleton-container">
        <Skeleton className="ranking-card-skeleton" variant="rectangular" />
      </div>
    );
  }

  if (error) {
    return renderCardContent(renderErrorCard(action ? () => action() : undefined));
  }

  if (!fetchPage && isEmptyObject(data)) {
    return renderCardContent(renderEmptyCard());
  }

  const suffixIconRender = (item: IRankingDataItem) => {
    switch (suffixIconType) {
      case 'circle-indicator':
        return (
          <BdsCircleIndicatorPictogram
            className={'circle-indicator-icon-ranking'}
            circleIndicatorSize="m"
            pictogram={{ url: item.icon ?? '' }}
          />
        );
      case 'default':
      default:
        return <img className="h-9 rounded-full" src={item.icon ?? ''} alt={item.title} />;
    }
  };

  const renderRankingItem = (item: IRankingDataItem, index: number) => (
    <div className="flex flex-column gap-y-6" key={item.id}>
      <div className="ranking-item flex flex-row justify-between items-center">
        <div className="flex gap-x-4 items-center">
          <div className={`ranking-position pos${highlightTopThree ? index + 1 : ''} flex justify-center h-8 w-8`}>
            <div className="position-number">{index + 1}</div>
          </div>
          <div className="flex flex-column">
            <div className="ranking-primary-text">{item.title}</div>
            <div className="ranking-secondary-text">
              {toLocaleNumberFormat(Number(item.value))}
              {isPercentage && '%'} {t(valueText, { count: Number(item.value) })}
            </div>
          </div>
        </div>
        {suffixIconRender(item)}
      </div>
      <BdsDivider color="light" size="s" />
    </div>
  );

  const renderPagedRanking = (fetchRankingPage: NonNullable<IRankingCard['fetchPage']>) => (
    <InfiniteScroll<IRankingDataItem>
      key={pagedListKey}
      fetchPage={fetchRankingPage}
      limit={pageSize}
      renderItem={renderRankingItem}
      loadingState={
        <div className="ranking-list-loading">
          <CircularProgress sx={{ color: CONTROLLER_ON }} size={24} />
          <div>{t(TRANSLATIONS_METRICS_NE.rankingCard.listLoading)}</div>
        </div>
      }
      finishedState={<div className="ranking-list-finished">{t(TRANSLATIONS_METRICS_NE.rankingCard.listLoaded)}</div>}
      emptyState={renderEmptyCard()}
      errorState={renderErrorCard(() => setPagedListKey((prev) => prev + 1))}
    />
  );

  return (
    <div className="ranking-card-content" data-experience="new">
      {renderRankingHeader()}
      {hasTabBar && (
        <div className="ranking-card-tab flex flex-row">
          <TabBar items={tabBarParams.items} action={tabBarParams.action} activeIndex={tabBarParams.value} />
        </div>
      )}
      <BdsDivider color="light" size="m" />
      <div className="ranking-benefits flex flex-column gap-y-6">
        {fetchPage ? renderPagedRanking(fetchPage) : data && rankingData?.map(renderRankingItem)}
      </div>
    </div>
  );
};

export default RankingCard;
